import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Loader2, ChevronLeft, Minus, Plus } from "lucide-react";
import { fetchProductByHandle, type ShopProductDetail } from "@/lib/shopify";
import { useCart } from "@/lib/cart";

export const Route = createFileRoute("/merch/$productHandle")({
  head: () => ({
    meta: [
      { title: "Product — Real Pranks Merch" },
      { name: "description", content: "Shop official Real Pranks merch." },
    ],
  }),
  component: ProductPage,
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="mx-auto max-w-2xl px-4 py-24 text-center">
          <p className="text-sm text-muted-foreground">Error: {error.message}</p>
          <Button
            className="mt-6"
            onClick={() => {
              router.invalidate();
              reset();
            }}
          >
            Retry
          </Button>
        </div>
      </div>
    );
  },
  notFoundComponent: () => (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="mx-auto max-w-2xl px-4 py-24 text-center">
        <h1 className="text-2xl font-bold">Product not found</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          The product you're looking for doesn't exist or has been removed.
        </p>
        <Button asChild className="mt-6">
          <Link to="/merch">Back to Merch</Link>
        </Button>
      </div>
    </div>
  ),
});

function formatMoney(amount: string, currencyCode: string) {
  const n = Number(amount);
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode || "USD",
    }).format(n);
  } catch {
    return `$${n.toFixed(2)}`;
  }
}

function ProductPage() {
  const { productHandle } = Route.useParams();
  const { addItem } = useCart();

  const [product, setProduct] = useState<ShopProductDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Per-option selections (e.g., { Size: "M", Color: "Black" })
  const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>({});
  const [activeImageIdx, setActiveImageIdx] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchProductByHandle(productHandle)
      .then((p) => {
        if (cancelled) return;
        setProduct(p);
        if (p) {
          // Initialize selected options from the first available variant
          const firstAvailable = p.variants.find((v) => v.availableForSale) ?? p.variants[0];
          if (firstAvailable) {
            const init: Record<string, string> = {};
            for (const so of firstAvailable.selectedOptions) {
              init[so.name] = so.value;
            }
            setSelectedOptions(init);
          }
        }
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message || "Failed to load product");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [productHandle]);

  // Match the variant whose selectedOptions matches every chosen option.
  const matchedVariant = useMemo(() => {
    if (!product) return null;
    const keys = Object.keys(selectedOptions);
    if (keys.length === 0) return product.variants[0] ?? null;
    return (
      product.variants.find((v) =>
        v.selectedOptions.every((so) => selectedOptions[so.name] === so.value),
      ) ?? null
    );
  }, [product, selectedOptions]);

  // Switch active image to variant image when variant changes.
  useEffect(() => {
    if (!product || !matchedVariant?.image) return;
    const idx = product.images.findIndex((img) => img.src === matchedVariant.image?.src);
    if (idx >= 0) setActiveImageIdx(idx);
  }, [matchedVariant, product]);

  const handleAdd = async () => {
    if (!matchedVariant) return;
    setAdding(true);
    try {
      await addItem(matchedVariant.id, quantity);
    } catch (e) {
      console.error("[product] add failed", e);
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="mx-auto max-w-6xl px-4 py-8">
        <Link
          to="/merch"
          className="mb-6 inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <ChevronLeft className="h-4 w-4" />
          Back to Merch
        </Link>

        {loading && (
          <div className="flex justify-center py-24">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        )}

        {error && !loading && (
          <div className="rounded-lg border border-border bg-card p-6 text-center text-sm text-muted-foreground">
            {error}
          </div>
        )}

        {!loading && !error && !product && (
          <div className="rounded-lg border border-border bg-card p-6 text-center text-sm text-muted-foreground">
            Product not found.
          </div>
        )}

        {!loading && product && (
          <div className="grid gap-10 lg:grid-cols-2">
            {/* Image gallery */}
            <div className="flex flex-col gap-3">
              <div className="aspect-square w-full overflow-hidden rounded-lg border border-border bg-secondary">
                {product.images[activeImageIdx] ? (
                  <img
                    src={product.images[activeImageIdx].src}
                    alt={product.images[activeImageIdx].altText ?? product.title}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="h-full w-full" />
                )}
              </div>
              {product.images.length > 1 && (
                <div className="grid grid-cols-5 gap-2">
                  {product.images.map((img, idx) => (
                    <button
                      key={img.src}
                      type="button"
                      onClick={() => setActiveImageIdx(idx)}
                      className={`aspect-square overflow-hidden rounded-md border-2 bg-secondary transition-colors ${
                        idx === activeImageIdx
                          ? "border-foreground"
                          : "border-border hover:border-foreground/50"
                      }`}
                      aria-label={`View image ${idx + 1}`}
                    >
                      <img
                        src={img.src}
                        alt={img.altText ?? `${product.title} ${idx + 1}`}
                        className="h-full w-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Product info */}
            <div className="flex flex-col">
              {product.vendor && (
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {product.vendor}
                </p>
              )}
              <h1 className="mt-1 text-2xl font-extrabold sm:text-3xl">{product.title}</h1>

              {matchedVariant && (
                <div className="mt-3 flex items-baseline gap-3">
                  <span className="text-2xl font-bold">
                    {formatMoney(
                      matchedVariant.price.amount,
                      matchedVariant.price.currencyCode,
                    )}
                  </span>
                  {matchedVariant.compareAtPrice &&
                    Number(matchedVariant.compareAtPrice.amount) >
                      Number(matchedVariant.price.amount) && (
                      <span className="text-sm text-muted-foreground line-through">
                        {formatMoney(
                          matchedVariant.compareAtPrice.amount,
                          matchedVariant.compareAtPrice.currencyCode,
                        )}
                      </span>
                    )}
                </div>
              )}

              {/* Option pickers */}
              {product.options
                .filter((opt) => !(opt.values.length === 1 && opt.values[0] === "Default Title"))
                .map((opt) => (
                  <div key={opt.id} className="mt-6">
                    <p className="mb-2 text-sm font-semibold">
                      {opt.name}:{" "}
                      <span className="font-normal text-muted-foreground">
                        {selectedOptions[opt.name] ?? "—"}
                      </span>
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {opt.values.map((value) => {
                        const isSelected = selectedOptions[opt.name] === value;
                        // Determine availability for this option value given other selections
                        const wouldMatch = product.variants.find((v) =>
                          v.selectedOptions.every((so) =>
                            so.name === opt.name
                              ? so.value === value
                              : selectedOptions[so.name] === so.value,
                          ),
                        );
                        const isUnavailable = !wouldMatch || !wouldMatch.availableForSale;
                        return (
                          <button
                            key={value}
                            type="button"
                            onClick={() =>
                              setSelectedOptions((prev) => ({ ...prev, [opt.name]: value }))
                            }
                            className={`rounded-md border px-3 py-1.5 text-sm font-medium transition-colors ${
                              isSelected
                                ? "border-foreground bg-foreground text-background"
                                : isUnavailable
                                  ? "border-border text-muted-foreground line-through opacity-60"
                                  : "border-border text-foreground hover:border-foreground/60"
                            }`}
                          >
                            {value}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}

              {/* Quantity */}
              <div className="mt-6">
                <p className="mb-2 text-sm font-semibold">Quantity</p>
                <div className="inline-flex items-center rounded-md border border-border">
                  <button
                    type="button"
                    onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                    className="px-3 py-2 text-muted-foreground hover:text-foreground"
                    aria-label="Decrease quantity"
                  >
                    <Minus className="h-3.5 w-3.5" />
                  </button>
                  <span className="w-10 text-center text-sm font-semibold">{quantity}</span>
                  <button
                    type="button"
                    onClick={() => setQuantity((q) => q + 1)}
                    className="px-3 py-2 text-muted-foreground hover:text-foreground"
                    aria-label="Increase quantity"
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>

              <Button
                type="button"
                onClick={handleAdd}
                disabled={!matchedVariant || !matchedVariant.availableForSale || adding}
                className="mt-6 w-full"
                size="lg"
              >
                {adding ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Adding…
                  </>
                ) : !matchedVariant ? (
                  "Unavailable"
                ) : !matchedVariant.availableForSale ? (
                  "Sold out"
                ) : (
                  "Add to Cart"
                )}
              </Button>

              {/* Description */}
              {product.descriptionHtml && (
                <div className="mt-8 border-t border-border pt-6">
                  <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider">
                    Description
                  </h2>
                  <div
                    className="prose prose-invert max-w-none text-sm text-muted-foreground [&_a]:text-foreground [&_a]:underline [&_p]:mb-3"
                    dangerouslySetInnerHTML={{ __html: product.descriptionHtml }}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
